import React from 'react'
import { Box } from '@mui/material'
import { Theme, alpha, useTheme } from '@mui/material/styles'

export type AiPulseBadgeColor =
  | 'white'
  | 'black'
  | 'primary'
  | 'neon'
  | 'aqua'
  | 'red'

const resolveColor = (theme: Theme, color: AiPulseBadgeColor): string => {
  switch (color) {
    case 'white':
      return theme.palette.common.white
    case 'black':
      return theme.palette.common.black
    case 'primary':
      return theme.palette.primary.main
    case 'neon':
      return theme.app.neon
    case 'aqua':
      return theme.app.aqua
    case 'red':
      return theme.palette.error.main
  }
}

export interface AiPulseBadgeProps {
  children: React.ReactNode
  textColor: AiPulseBadgeColor
  accentColor: AiPulseBadgeColor
}

export const AiPulseBadge: React.FC<AiPulseBadgeProps> = ({
  children,
  textColor,
  accentColor,
}) => {
  const theme = useTheme()
  const text = resolveColor(theme, textColor)
  const accent = resolveColor(theme, accentColor)
  const background = alpha(accent, 0.3)
  const border = accentColor === 'red' ? alpha(accent, 0.5) : accent
  const haloRest = alpha(accent, 0.4)
  const haloPeak = alpha(accent, 0.35)

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: background,
        border: '1.5px solid',
        borderColor: border,
        borderRadius: 24,
        fontFamily: theme.app.monoFontFamily,
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '1.2px',
        color: text,
        willChange: 'transform, box-shadow',
        animation: 'aiPulseBadge 2.6s ease-in-out infinite',
        '@keyframes aiPulseBadge': {
          '0%, 100%': {
            transform: 'scale(1)',
            boxShadow: `0 0 0 0 ${haloRest}`,
          },
          '50%': {
            transform: 'scale(1.03)',
            boxShadow: `0 0 18px 6px ${haloPeak}`,
          },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: accent,
          flexShrink: 0,
        }}
      />
      {children}
    </Box>
  )
}
